import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Divider,
  Grid,
  Paper,
  CircularProgress as Redirecting,
  Typography,
  makeStyles,
} from '@material-ui/core';
import Description from '@material-ui/icons/Description';
import AcceptTermsDialog from 'components/shared/AcceptTermsDialog';
import CircularProgress from 'components/shared/CircularProgress';
import FormatPrice from 'components/shared/FormatPrice';
import CreditCardForm from './CreditCardForm';
import PaymentDone from './PaymentDone';
import { newGetTotalPrice } from '../helpers';
import PlansFormSection from './PlansFormSection';
import AddonsFormSection from './AddonsFormSection';

const styles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  info: {
    padding: theme.spacing(2),
  },
  block: {
    padding: theme.spacing(3),
    position: 'relative',
  },
  blockTitle: {
    color: theme.palette.grey[500],
    marginBottom: theme.spacing(1),
  },
  icon: {
    marginRight: theme.spacing(0.5),
  },
  methodButton: {
    flex: 1,
    fontWeight: 400,
    textTransform: 'unset',
    color: theme.palette.grey[500],
    borderWidth: 2,

    '&:hover': {
      borderWidth: 2,
    },
  },
  methodButtonActive: {
    color: theme.palette.primary[500],
    boxShadow: '0 0 8px rgba(0, 0, 0, 0.1)',
  },
  button: {
    marginTop: theme.spacing(0.5),
  },
  btnInner: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const Checkout = ({
  activeStep,
  cottage,
  isLoading,
  isRedirecting, // pre-redirect spinner
  isUpdating, // disable buttons, Elements flow only
  isVerifyingPayment, // disable buttons + "verifying" spinner
  options,
  order,
  paymentMethod,
  source,
  isBillogramEnabled,
  isStripeCheckoutEnabled,
  setPaymentMethod,
  submitCommission,
  submitForm,
}) => {
  const classes = styles();
  const [acceptTermsOpen, setAcceptTermsOpen] = React.useState(false);
  const [formValues, setFormValues] = React.useState({
    payment: '',
    gallery: '',
    en: false,
    de: false,
  });

  const onChange = (name) => (value) => {
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const openAcceptTermsDialog = () => {
    setAcceptTermsOpen(true);
  };

  const closeAccepTermsDialog = () => {
    setAcceptTermsOpen(false);
  };

  const onSubmitCommission = () => {
    submitCommission();
    closeAccepTermsDialog();
  };

  const totalPrice = newGetTotalPrice(formValues, options);
  const someSelected = Object.values(formValues).some((v) => !!v);
  const fixedSelected =
    !options.find(({ id }) => id === 1) || parseInt(formValues.payment, 10) === 1;

  const plans = options.filter(({ type }) => !type);
  const addons = options.filter(({ type }) => type);

  // Loading flags
  const checkoutLoading = isStripeCheckoutEnabled && (isRedirecting || isVerifyingPayment);
  const elementsLoading = !isStripeCheckoutEnabled && isUpdating;
  const uiLoading = checkoutLoading || elementsLoading;

  const startIcon = uiLoading ? <Redirecting size={24} color="inherit" /> : null;

  function getButtonText() {
    if (isStripeCheckoutEnabled) {
      if (isRedirecting) {
        return (
          <FormattedMessage
            id="payment.redirecting"
            defaultMessage="Redirecting you to payment..."
          />
        );
      }
      if (isVerifyingPayment) {
        return (
          <FormattedMessage id="payment.verifyingPayment" defaultMessage="Verifying payment..." />
        );
      }
      return <FormattedMessage id="Continue" defaultMessage="Continue" />;
    }
    return elementsLoading ? (
      <FormattedMessage id="payment.processing" defaultMessage="Processing payment..." />
    ) : (
      <FormattedMessage id="Continue" defaultMessage="Continue" />
    );
  }

  function getTestId() {
    if (isStripeCheckoutEnabled) {
      if (isRedirecting) return 'checkout-redirecting';
      if (isVerifyingPayment) return 'checkout-verifying';
      return 'checkout-continue';
    }
    return elementsLoading ? 'elements-processing' : 'checkout-continue';
  }

  const buttonText = getButtonText();
  const testId = getTestId();

  const isPlanVisible = !!plans.length;
  const onSubmitForm = () => {
    submitForm(formValues, totalPrice);
  };

  if (isLoading || (source && activeStep !== 2)) {
    return <CircularProgress />;
  }

  // Done page
  if (activeStep === 2) {
    return <PaymentDone cottage={cottage} order={order} paymentMethod={paymentMethod} />;
  }

  return (
    <div className={classes.root}>
      {isPlanVisible && cottage.paid_until && (
        <div className={classes.info}>
          <Typography variant="subtitle1">
            <FormattedMessage id="payment.adPaidUntil" defaultMessage="Ad is paid until" />:{' '}
            {cottage.paid_until}
          </Typography>
        </div>
      )}
      {isPlanVisible && (
        <>
          <Typography variant="subtitle1" className={classes.blockTitle}>
            <FormattedMessage
              id="payment.comissionTitle"
              defaultMessage="Select preferred package"
            />
          </Typography>
          <PlansFormSection
            disabled={activeStep > 0}
            plans={plans}
            onChange={onChange}
            isCurrentFixed={cottage.reviewed_at && cottage.commission === false}
            formValues={formValues}
          />
        </>
      )}
      {!!addons.length && fixedSelected && (
        <>
          <Typography variant="subtitle1" className={classes.blockTitle} align="center">
            <FormattedMessage id="payment.selectAddons" defaultMessage="Addon - translations" />
          </Typography>
          <AddonsFormSection
            onChange={onChange}
            formValues={formValues}
            addons={addons}
            disabled={activeStep > 0}
          />
        </>
      )}
      <Grid container justifyContent="center">
        <Grid item xs={12} md={6} lg={4}>
          <Paper>
            {fixedSelected && (
              <>
                <div className={classes.block}>
                  <Grid container spacing={2}>
                    {isBillogramEnabled && (
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          color={paymentMethod === 'invoice' ? 'primary' : undefined}
                          className={classNames(
                            classes.methodButton,
                            paymentMethod === 'invoice' && classes.methodButtonActive,
                          )}
                          variant="outlined"
                          onClick={() => setPaymentMethod('invoice')}
                        >
                          <Description className={classes.icon} />
                          <div>
                            <FormattedMessage id="Invoice" defaultMessage="Invoice" />
                          </div>
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </div>
                <Divider />
                <div className={classes.block}>
                  {isBillogramEnabled && paymentMethod === 'invoice' && (
                    <Typography paragraph>
                      <FormattedMessage
                        id="payment.invoiceInfo"
                        defaultMessage="The ad will be updated when the invoice is paid.
                          If you want your ad to be updated directly, use card payment instead,
                          you will receive a receipt immediately when using card.
                          We will not update the ad until the invoice is paid. An invoice fee of 40 SEK will be added."
                      />
                    </Typography>
                  )}
                  <>
                    {!isStripeCheckoutEnabled && (
                      <Typography variant="subtitle1" className={classes.blockTitle}>
                        <FormattedMessage id="Details" defaultMessage="Details" />
                      </Typography>
                    )}
                    {isStripeCheckoutEnabled && (
                      <Typography paragraph align="center">
                        <FormattedMessage
                          id="payment.stripeCheckoutInfo"
                          defaultMessage="Click Pay to proceed to our secure checkout for payment"
                        />
                      </Typography>
                    )}
                  </>
                  {!paymentMethod && !isStripeCheckoutEnabled && (
                    <div style={{ display: paymentMethod ? 'none' : 'block' }}>
                      <CreditCardForm />
                    </div>
                  )}
                </div>
                <div className={classes.button}>
                  {isRedirecting ? (
                    // Pre-redirect spinner
                    <Button
                      fullWidth
                      color="primary"
                      size="large"
                      variant="contained"
                      disabled
                      startIcon={<Redirecting size={24} color="inherit" />}
                      aria-busy="true"
                      aria-live="polite"
                      data-testid="checkout-redirecting"
                    >
                      <FormattedMessage
                        id="payment.redirecting"
                        defaultMessage="Redirecting you to payment..."
                      />
                    </Button>
                  ) : (
                    // Idle or loading after return (verifying) or Elements processing
                    <Button
                      fullWidth
                      color="primary"
                      disabled={uiLoading || !someSelected}
                      size="large"
                      variant="contained"
                      onClick={onSubmitForm}
                      startIcon={startIcon}
                      aria-busy={uiLoading ? 'true' : undefined}
                      aria-live="polite"
                      data-testid={testId}
                    >
                      {uiLoading ? (
                        buttonText
                      ) : (
                        <>
                          {!paymentMethod && (
                            <>
                              <FormattedMessage id="Pay" defaultMessage="Pay" />{' '}
                              <FormatPrice price={totalPrice} />
                            </>
                          )}
                          {isBillogramEnabled && paymentMethod === 'invoice' && (
                            <>
                              <FormattedMessage id="Get invoice" defaultMessage="Get invoice" /> (
                              <FormatPrice price={totalPrice + 4000} />)
                            </>
                          )}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </>
            )}
            {!fixedSelected && (
              <div className={classes.button}>
                <Button
                  fullWidth
                  color="primary"
                  disabled={uiLoading || !someSelected}
                  size="large"
                  variant="contained"
                  onClick={openAcceptTermsDialog}
                  startIcon={startIcon}
                  aria-busy={uiLoading ? 'true' : undefined}
                  aria-live="polite"
                  data-testid={testId}
                >
                  {buttonText}
                </Button>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>
      <AcceptTermsDialog
        open={acceptTermsOpen}
        onClose={closeAccepTermsDialog}
        onConfirm={onSubmitCommission}
      />
    </div>
  );
};

Checkout.propTypes = {
  activeStep: PropTypes.number,
  cottage: PropTypes.shape({
    paid_until: PropTypes.string,
    reviewed_at: PropTypes.string,
    commission: PropTypes.bool,
  }).isRequired,
  isLoading: PropTypes.bool,
  isRedirecting: PropTypes.bool,
  isVerifyingPayment: PropTypes.bool,
  isUpdating: PropTypes.bool,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  order: PropTypes.shape({}),
  paymentMethod: PropTypes.oneOf(['invoice']),
  source: PropTypes.string,
  isBillogramEnabled: PropTypes.bool,
  isStripeCheckoutEnabled: PropTypes.bool,
  setPaymentMethod: PropTypes.func.isRequired,
  submitCommission: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
};

Checkout.defaultProps = {
  order: {},
  isBillogramEnabled: false,
  isStripeCheckoutEnabled: false,
};

export default Checkout;
